class WishlistObjectQf {
  constructor(container = document) {
    // The container defines the scope of this instance
    this.container = container;

    // ---------------------------------
    // CONFIG
    // ---------------------------------

    this.WISHLIST_API_URL = '/apps/customer-wishlist';

    this.LOCAL_STORAGE_CLIENT_NAME = window.Shopify.shop.replace(
      /\.myshopify\.com$/i,
      ''
    );

    this.LOCAL_STORAGE_WISHLIST_KEY =
      this.LOCAL_STORAGE_CLIENT_NAME + '-wishlist';

    // ---------------------------------
    // ACCOUNT STATE
    // ---------------------------------

    this.accountWishlistEnabled = false;
    this.accountWishlistLoaded = false;

    /*
      Start from localStorage so guest users still get
      immediate wishlist functionality.

      If the customer is logged in, this will shortly
      be replaced by the Shopify account wishlist.
    */
    this.wishlist = this.getLocalWishlist();

    // ---------------------------------
    // GRID RENDER PROTECTION
    // ---------------------------------

    /*
      initGrid() fetches product cards asynchronously.

      Without this, an OLD localStorage render can finish
      AFTER the account wishlist render and overwrite it.

      Each grid gets its own render number.
    */
    this.gridRenderVersions = new WeakMap();

    // ---------------------------------
    // CLASSES / SELECTORS
    // ---------------------------------

    this.BUTTON_ACTIVE_CLASS = 'active-wishlist';
    this.GRID_LOADED_CLASS = 'loaded';
    this.PRODUCT_CARD = 'product-card-nocarousel';

    this.selectors = {
      button: '[button-wishlist]',
      clearWishlist: '.clear-wishlist',
      grid: '[grid-wishlist]',
      gridContainer: '.wishlist-grid-container',
      sideCartGrid: '[side-cart-grid-wishlist]',
      productCard: '.product-card-nocarousel',
      wishlistCount: '.wishlistCount',
      emptyWishlist: '.empty-wishlist',
    };

    // Only bind global events once
    if (!WishlistObjectQf.globalListenersBound) {
      this.bindGlobalListeners();
      WishlistObjectQf.globalListenersBound = true;
    }

    this.init();
  }


  // ---------------------------------
  // INITIALIZATION
  // ---------------------------------

  async init() {
    /*
      Render whatever we have locally immediately.

      This keeps guest wishlist functionality fast and
      avoids waiting for the API before hearts work.
    */
    this.refreshWishlistUI();

    /*
      Then check Shopify.

      If logged in, Shopify becomes authoritative and
      the UI is rendered again from that account wishlist.
    */
    await this.initAccountWishlist();
  }


  // ---------------------------------
  // GLOBAL EVENT LISTENERS
  // ---------------------------------

  bindGlobalListeners() {
    document.body.addEventListener('click', async (e) => {
      const button = e.target.closest(this.selectors.button);

      if (button) {
        e.preventDefault();

        const product = this.getProductFromButton(button);

        if (!product.handle) {
          return console.error(
            '[Shopify Wishlist] Missing `data-product-handle` attribute.'
          );
        }

        await this.toggleWishlistItem(product);
      }

      const clearButton = e.target.closest(
        this.selectors.clearWishlist
      );

      if (clearButton) {
        e.preventDefault();
        await this.resetWishlist();
      }
    });

    window.addEventListener(
      'desktop-cart-recommendations-rendered',
      () => {
        this.updateAllButtons();
      }
    );
  }


  // ---------------------------------
  // LOCAL STORAGE
  // ---------------------------------

  getLocalWishlist() {
    try {
      const stored =
        localStorage.getItem(
          this.LOCAL_STORAGE_WISHLIST_KEY
        ) || '[]';

      const wishlist = JSON.parse(stored);

      if (!Array.isArray(wishlist)) {
        return [];
      }

      return wishlist
        .map((item) => {
          /*
            Support our normal object format:
            {
              handle: "...",
              attributes: {}
            }

            Also tolerate plain string handles.
          */
          if (typeof item === 'string') {
            return {
              handle: item,
              attributes: {},
            };
          }

          if (
            item &&
            typeof item.handle === 'string'
          ) {
            return {
              handle: item.handle,
              attributes: item.attributes || {},
            };
          }

          return null;
        })
        .filter(Boolean);
    } catch (error) {
      console.error(
        '[Shopify Wishlist] Could not read local wishlist:',
        error
      );

      return [];
    }
  }


  setLocalWishlist(array) {
    try {
      if (array.length) {
        localStorage.setItem(
          this.LOCAL_STORAGE_WISHLIST_KEY,
          JSON.stringify(array)
        );
      } else {
        localStorage.removeItem(
          this.LOCAL_STORAGE_WISHLIST_KEY
        );
      }
    } catch (error) {
      console.error(
        '[Shopify Wishlist] Could not save local wishlist:',
        error
      );
    }
  }


  // ---------------------------------
  // ACCOUNT WISHLIST
  // ---------------------------------

  async initAccountWishlist() {
    try {
      const response = await fetch(
        this.WISHLIST_API_URL,
        {
          method: 'GET',

          headers: {
            Accept: 'application/json',
          },

          credentials: 'same-origin',

          /*
            Always ask the account endpoint for the
            current value rather than a cached response.
          */
          cache: 'no-store',
        }
      );

      if (!response.ok) {
        throw new Error(
          `Account wishlist request failed: ${response.status}`
        );
      }

      const data = await response.json();

      console.log(
        '[Shopify Wishlist] Account response:',
        data
      );


      // ---------------------------------
      // GUEST
      // ---------------------------------

      /*
        If there is no logged-in customer,
        localStorage remains the source of truth.
      */
      if (!data.loggedIn) {
        this.accountWishlistEnabled = false;
        this.accountWishlistLoaded = true;

        this.wishlist =
          this.getLocalWishlist();

        this.refreshWishlistUI();

        return;
      }


      // ---------------------------------
      // LOGGED-IN CUSTOMER
      // ---------------------------------

      this.accountWishlistEnabled = true;
      this.accountWishlistLoaded = true;

      /*
        Shopify returns:

        [
          "product-handle-one",
          "product-handle-two"
        ]

        Convert that into the object structure used
        throughout the existing wishlist code.
      */
      const accountWishlist =
        Array.isArray(data.wishlist)
          ? data.wishlist
              .filter(
                (handle) =>
                  typeof handle === 'string' &&
                  handle.trim() !== ''
              )
              .map((handle) => ({
                handle: handle.trim(),
                attributes: {},
              }))
          : [];


      /*
        IMPORTANT:

        Once logged in, the Shopify customer account
        is the authoritative wishlist.

        We deliberately do NOT merge arbitrary existing
        browser localStorage here.

        This is what gives us consistent cross-device
        behaviour.
      */
      this.wishlist = accountWishlist;


      /*
        localStorage now becomes a cache of the
        account wishlist on this browser.
      */
      this.setLocalWishlist(
        accountWishlist
      );


      console.log(
        '[Shopify Wishlist] Using account wishlist:',
        this.wishlist
      );


      /*
        Update:
        - all hearts/buttons
        - wishlist count
        - wishlist grid
      */
      this.refreshWishlistUI();

    } catch (error) {
      /*
        If account sync fails for any reason,
        wishlist functionality should still work.

        Fall back to browser storage.
      */
      console.error(
        '[Shopify Wishlist] Account sync unavailable. Falling back to localStorage:',
        error
      );

      this.accountWishlistEnabled = false;
      this.accountWishlistLoaded = true;

      this.wishlist =
        this.getLocalWishlist();

      this.refreshWishlistUI();
    }
  }


  async saveAccountWishlist(wishlist) {
    if (!this.accountWishlistEnabled) {
      return;
    }

    const handles = wishlist
      .map((item) => item.handle)
      .filter(Boolean);

    const response = await fetch(
      this.WISHLIST_API_URL,
      {
        method: 'POST',

        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },

        credentials: 'same-origin',

        body: JSON.stringify({
          wishlist: handles,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(
        data?.error ||
          'Could not save account wishlist.'
      );
    }

    return data;
  }


  // ---------------------------------
  // WISHLIST NORMALIZATION
  // ---------------------------------

  cleanWishlist(array) {
    const unique = new Map();

    array.forEach((item) => {
      if (!item) return;

      let handle = null;
      let attributes = {};

      if (typeof item === 'string') {
        handle = item;
      } else if (
        typeof item.handle === 'string'
      ) {
        handle = item.handle;
        attributes =
          item.attributes || {};
      }

      if (!handle) return;

      handle = handle.trim();

      if (!handle) return;

      unique.set(handle, {
        handle,
        attributes,
      });
    });

    return Array.from(
      unique.values()
    );
  }


  // ---------------------------------
  // WISHLIST MANAGEMENT
  // ---------------------------------

  getWishlist() {
    return this.wishlist;
  }


  async setWishlist(array) {
    const cleanedWishlist =
      this.cleanWishlist(array);

    /*
      Update in-memory wishlist immediately.
    */
    this.wishlist =
      cleanedWishlist;

    /*
      Always update localStorage.

      Guest:
      localStorage is the actual wishlist.

      Logged in:
      localStorage is a local cache.
    */
    this.setLocalWishlist(
      cleanedWishlist
    );

    /*
      Update the UI immediately.
    */
    this.refreshWishlistUI();

    /*
      Logged-in users also save to Shopify.
    */
    if (this.accountWishlistEnabled) {
      try {
        await this.saveAccountWishlist(
          cleanedWishlist
        );
      } catch (error) {
        console.error(
          '[Shopify Wishlist] Could not sync wishlist to customer account:',
          error
        );
      }
    }
  }


  async toggleWishlistItem(product) {
    const wishlist = [
      ...this.getWishlist(),
    ];

    const index =
      wishlist.findIndex(
        (item) =>
          item.handle ===
          product.handle
      );

    if (index === -1) {
      wishlist.push(product);
    } else {
      wishlist.splice(
        index,
        1
      );
    }

    await this.setWishlist(
      wishlist
    );
  }


  wishlistContains(handle) {
    if (!handle) {
      return false;
    }

    return this.getWishlist().some(
      (item) =>
        item.handle === handle
    );
  }


  async resetWishlist() {
    await this.setWishlist([]);
  }


  // ---------------------------------
  // PRODUCT HELPERS
  // ---------------------------------

  getProductFromButton(button) {
    const attributes = {};

    this.container
      .querySelectorAll(
        "input[type='radio']"
      )
      .forEach((radio) => {
        if (radio.checked) {
          attributes[radio.name] =
            radio.value;
        }
      });

    return {
      handle:
        button.dataset.productHandle ||
        false,

      attributes,
    };
  }


  // ---------------------------------
  // BUTTONS
  // ---------------------------------

  updateButtons() {
    this.container
      .querySelectorAll(
        this.selectors.button
      )
      .forEach((button) => {
        const handle =
          button.dataset.productHandle;

        button.classList.toggle(
          this.BUTTON_ACTIVE_CLASS,
          this.wishlistContains(handle)
        );
      });
  }


  updateAllButtons() {
    document
      .querySelectorAll(
        this.selectors.button
      )
      .forEach((button) => {
        const handle =
          button.dataset.productHandle;

        button.classList.toggle(
          this.BUTTON_ACTIVE_CLASS,
          this.wishlistContains(handle)
        );
      });
  }


  // ---------------------------------
  // WISHLIST COUNT
  // ---------------------------------

  updateWishlistCount() {
    const count =
      this.getWishlist().length;

    this.container
      .querySelectorAll(
        this.selectors.wishlistCount
      )
      .forEach((el) => {
        el.textContent = count;

        el.classList.toggle(
          'is-hidden',
          count === 0
        );
      });
  }


  updateAllWishlistCounts() {
    const count =
      this.getWishlist().length;

    document
      .querySelectorAll(
        this.selectors.wishlistCount
      )
      .forEach((el) => {
        el.textContent = count;

        el.classList.toggle(
          'is-hidden',
          count === 0
        );
      });
  }


  // ---------------------------------
  // COMPLETE UI REFRESH
  // ---------------------------------

  refreshWishlistUI() {
    this.updateAllButtons();
    this.updateAllWishlistCounts();

    document
      .querySelectorAll(
        this.selectors.grid
      )
      .forEach((grid) => {
        this.initGrid(grid);
      });
  }


  // ---------------------------------
  // GRID INITIALIZATION
  // ---------------------------------

  initGrids() {
    this.container
      .querySelectorAll(
        this.selectors.grid
      )
      .forEach((grid) => {
        this.initGrid(grid);
      });
  }


  async initGrid(grid) {
    /*
      Create a new render number for THIS grid.

      Any older async render still running will be
      ignored when it finishes.
    */
    const currentVersion =
      (this.gridRenderVersions.get(
        grid
      ) || 0) + 1;

    this.gridRenderVersions.set(
      grid,
      currentVersion
    );


    /*
      Snapshot the wishlist for this particular render.
    */
    const wishlist =
      this.getWishlist().map(
        (item) => ({
          handle: item.handle,
          attributes:
            item.attributes || {},
        })
      );


    const gridContainer =
      grid.closest(
        this.selectors.gridContainer
      );


    const fallback =
      gridContainer
        ? gridContainer.querySelector(
            this.selectors.emptyWishlist
          )
        : null;


    // ---------------------------------
    // EMPTY WISHLIST
    // ---------------------------------

    if (!wishlist.length) {
      /*
        Only update the DOM if this render is
        still the newest render.
      */
      if (
        this.gridRenderVersions.get(
          grid
        ) !== currentVersion
      ) {
        return;
      }

      grid.innerHTML = '';

      grid.classList.add(
        this.GRID_LOADED_CLASS
      );

      if (fallback) {
        fallback.classList.add(
          'show'
        );
      }

      return;
    }


    if (fallback) {
      fallback.classList.remove(
        'show'
      );
    }


    grid.classList.remove(
      this.GRID_LOADED_CLASS
    );


    const handles =
      wishlist.map(
        (item) => item.handle
      );


    console.log(
      '[Shopify Wishlist] Rendering handles:',
      handles
    );


    const productsHTML =
      await Promise.all(
        handles.map(
          async (handle) => {
            try {
              const url =
                window.Shopify.routes.root +
                `products/${encodeURIComponent(
                  handle
                )}?view=card&section_id=${this.PRODUCT_CARD}`;


              const res =
                await fetch(
                  url,
                  {
                    credentials:
                      'same-origin',
                  }
                );


              if (!res.ok) {
                throw new Error(
                  `HTTP ${res.status}`
                );
              }


              const html =
                await res.text();


              const doc =
                new DOMParser()
                  .parseFromString(
                    html,
                    'text/html'
                  );


              const card =
                doc.querySelector(
                  this.selectors.productCard
                );


              if (!card) {
                console.warn(
                  `[Shopify Wishlist] Product card not found for "${handle}".`
                );

                return null;
              }


              /*
                Make any wishlist buttons inside the
                returned product card active.
              */
              card
                .querySelectorAll(
                  this.selectors.button
                )
                .forEach((btn) => {
                  btn.classList.add(
                    this.BUTTON_ACTIVE_CLASS
                  );
                });


              return card.outerHTML;

            } catch (error) {
              console.error(
                `[Shopify Wishlist] Could not load product "${handle}":`,
                error
              );

              return null;
            }
          }
        )
      );


    /*
      CRITICAL:

      If another wishlist render started while
      these products were downloading, this data
      is now stale.

      Do NOT overwrite the newer wishlist.
    */
    if (
      this.gridRenderVersions.get(
        grid
      ) !== currentVersion
    ) {
      console.log(
        '[Shopify Wishlist] Ignoring stale grid render.'
      );

      return;
    }


    grid.innerHTML =
      productsHTML
        .filter(Boolean)
        .join('');


    grid.classList.add(
      this.GRID_LOADED_CLASS
    );


    /*
      Make sure newly injected hearts reflect
      the current wishlist.
    */
    this.updateAllButtons();
  }
}


// ---------------------------------
// SINGLETON INSTANCE
// ---------------------------------

if (!window.wishlist) {
  window.wishlist =
    new WishlistObjectQf();
}